import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  required,
  SelectInput,
  useListContext,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdCheck, MdClose, MdContentCopy, MdInfoOutline } from 'react-icons/md';
import { QRCodeCanvas } from 'qrcode.react';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import FilterGroup from 'src/pages/admin/components/FilterGroup';
import {
  listProviders,
  ProviderItem,
  updateHotWalletThreshold,
} from '../../../services/xbitRelayService';
import { XBIT_RELAY_HOT_WALLETS_PERMISSION_RESOURCE } from './constants';
import {
  HotWalletRow,
  RebalanceLockReason,
  ThresholdConfigEdit,
  buildThresholdConfigBody,
  isAlertConfigLocked,
  isRebalanceRangeValid,
  isValidConfigDecimal,
  rebalanceLockReason,
} from './helpers';
import './init';

const DEFAULT_PROVIDER = 'INTERNAL_PERPETUAL';

const truncateAddress = (addr: string) =>
  addr && addr.length > 14 ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : addr || '-';

type ProviderFilterProps = {
  providers: ProviderItem[];
  [key: string]: any;
};

type DepositInfo = {
  motherAddress: string;
  tokenName: string;
  tokenAddress: string;
  isNative: boolean;
  chainName?: string;
};

const ListFilter = ({ providers, ...rest }: ProviderFilterProps) => (
  <Filter {...rest}>
    <FilterGroup alwaysOn spacing={0} p={2}>
      <SelectInput
        source="provider"
        label="module.xbitRelayHotWallets.provider"
        choices={providers.map((p) => ({ id: p.provider, name: p.provider }))}
        sx={{ minWidth: 240 }}
        variant="outlined"
        validate={required()}
      />
    </FilterGroup>
  </Filter>
);

/**
 * Every save goes through here. `threshold-config` is a full replace, so the body is always
 * rebuilt from the whole row — see `buildThresholdConfigBody`.
 */
const useThresholdConfigSave = (record: HotWalletRow) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);

  const save = async (edit: ThresholdConfigEdit) => {
    setSaving(true);
    try {
      await updateHotWalletThreshold(record.provider, buildThresholdConfigBody(record, edit));
      notify('module.xbitRelayHotWallets.thresholdUpdated', { type: 'success' });
      refresh();
    } catch {
      notify('module.xbitRelayHotWallets.thresholdUpdateFailed', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
};

/**
 * Every inline edit here sends PUT /xbit-relay/hot-wallets/:provider/threshold-config, which the
 * BE gates on xbit-relay:hot-wallets:write. Read-only operators keep the values visible but
 * cannot change them.
 */
const useCanWriteThresholdConfig = (): boolean => {
  const { permissions } = usePermissions<PermissionData>();
  return hasPermission(
    permissions?.permissions,
    XBIT_RELAY_HOT_WALLETS_PERMISSION_RESOURCE,
    'write'
  );
};

type InlineDecimalEditProps = {
  record: HotWalletRow;
  value: string;
  toEdit(next: string): ThresholdConfigEdit;
  invalid?: boolean;
  locked?: boolean;
  title?: string;
};

/** Shared save/revert affordance for the three decimal columns. */
const InlineDecimalEdit = ({
  record,
  value,
  toEdit,
  invalid,
  locked,
  title,
}: InlineDecimalEditProps) => {
  const { save, saving } = useThresholdConfigSave(record);
  const canWrite = useCanWriteThresholdConfig();
  const [draft, setDraft] = useState<string>(value ?? '');
  const isDirty = draft !== (value ?? '');
  // The BE rejects anything outside its decimal grammar with a 400, so block the save here.
  const isInvalid = !isValidConfigDecimal(draft);

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <TextField
        size="small"
        value={draft}
        onChange={(e) => {
          const next = e.target.value;
          if (next === '' || /^\d*\.?\d*$/.test(next)) setDraft(next);
        }}
        disabled={saving || locked || !canWrite}
        error={!locked && isDirty && (isInvalid || invalid)}
        title={title}
        inputProps={{ style: { fontFamily: 'monospace', width: 90 } }}
        sx={{ width: 120 }}
      />
      {isDirty && !locked && canWrite && (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => save(toEdit(draft))}
            disabled={saving || isInvalid || invalid}
          >
            <MdCheck size={16} />
          </IconButton>
          <IconButton size="small" onClick={() => setDraft(value ?? '')} disabled={saving}>
            <MdClose size={16} />
          </IconButton>
        </>
      )}
    </Stack>
  );
};

const ColumnHeader = ({ label, hint }: { label: string; hint: string }) => {
  const t = useTranslate();
  return (
    <Tooltip title={t(hint)}>
      <Stack
        component="span"
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ cursor: 'help', whiteSpace: 'nowrap' }}
      >
        <span>{t(label)}</span>
        <MdInfoOutline size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
      </Stack>
    </Tooltip>
  );
};

const REBALANCE_LOCK_HINT: Record<RebalanceLockReason, string> = {
  chain: 'module.xbitRelayHotWallets.rebalanceUnsupported',
  native: 'module.xbitRelayHotWallets.rebalanceNativeUnsupported',
};

/** Wraps a disabled control so the tooltip still fires — MUI drops events on disabled elements. */
const LockedHint = ({ hint, children }: { hint: string; children: ReactElement }) => (
  <Tooltip title={hint}>
    <span>{children}</span>
  </Tooltip>
);

const InlineThresholdEdit = ({ record }: { record: HotWalletRow }) => {
  const t = useTranslate();
  const locked = isAlertConfigLocked(record.chainId);
  const field = (
    <InlineDecimalEdit
      record={record}
      value={record.alertThreshold ?? ''}
      toEdit={(threshold) => ({ threshold })}
      locked={locked}
    />
  );

  if (!locked) return field;
  return (
    <LockedHint hint={t('module.xbitRelayHotWallets.alertConfigUnsupported')}>{field}</LockedHint>
  );
};

const InlineCeilingEdit = ({ record }: { record: HotWalletRow }) => {
  const t = useTranslate();
  const reason = rebalanceLockReason(record);
  const field = (
    <InlineDecimalEdit
      record={record}
      value={record.rebalanceCeiling ?? ''}
      toEdit={(ceiling) => ({ ceiling })}
      locked={Boolean(reason)}
      title={reason ? undefined : t('module.xbitRelayHotWallets.rebalanceCeilingHint')}
    />
  );

  if (!reason) return field;
  return <LockedHint hint={t(REBALANCE_LOCK_HINT[reason])}>{field}</LockedHint>;
};

const InlineTargetEdit = ({ record }: { record: HotWalletRow }) => {
  const t = useTranslate();
  const reason = rebalanceLockReason(record);
  const field = (
    <InlineDecimalEdit
      record={record}
      value={record.rebalanceTarget ?? ''}
      toEdit={(target) => ({ target })}
      locked={Boolean(reason)}
      title={reason ? undefined : t('module.xbitRelayHotWallets.rebalanceTargetHint')}
    />
  );

  if (!reason) return field;
  return <LockedHint hint={t(REBALANCE_LOCK_HINT[reason])}>{field}</LockedHint>;
};

const InlineEnabledToggle = ({ record }: { record: HotWalletRow }) => {
  const t = useTranslate();
  const { save, saving } = useThresholdConfigSave(record);
  const canWrite = useCanWriteThresholdConfig();
  const locked = isAlertConfigLocked(record.chainId);

  return (
    // The centring wrapper sits outside the Tooltip so the hint stays anchored to the switch
    // itself rather than to the full cell width.
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Tooltip title={locked ? t('module.xbitRelayHotWallets.alertConfigUnsupported') : ''}>
        <span>
          <Switch
            checked={record.alertEnabled ?? false}
            onChange={(_, enabled) => save({ enabled })}
            disabled={saving || locked || !canWrite}
            size="small"
          />
        </span>
      </Tooltip>
    </Box>
  );
};

const InlineRebalanceToggle = ({ record }: { record: HotWalletRow }) => {
  const t = useTranslate();
  const { save, saving } = useThresholdConfigSave(record);
  const canWrite = useCanWriteThresholdConfig();

  // Turning auto-rebalance on with an unusable threshold/target/ceiling would just fail silently
  // upstream. Editing the three numbers stays unblocked so they can be fixed in any order.
  const rangeValid = isRebalanceRangeValid(
    record.alertThreshold,
    record.rebalanceTarget,
    record.rebalanceCeiling
  );
  const enabled = record.rebalanceEnabled ?? false;
  const reason = rebalanceLockReason(record);
  const blocked = Boolean(reason) || (!enabled && !rangeValid);
  // An unsupported chain or a native token outranks a range problem: fixing the numbers would
  // not unlock the switch, so pointing at them would send the operator the wrong way.
  const hint = reason
    ? t(REBALANCE_LOCK_HINT[reason])
    : t('module.xbitRelayHotWallets.rebalanceRangeInvalid');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Tooltip title={blocked ? hint : ''}>
        <span>
          <Switch
            checked={enabled}
            onChange={(_, rebalanceEnabled) => save({ rebalanceEnabled })}
            disabled={saving || blocked || !canWrite}
            size="small"
          />
        </span>
      </Tooltip>
    </Box>
  );
};

const XbitRelayHotWalletsList = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [providers, setProviders] = useState<ProviderItem[]>([]);
  const [depositInfo, setDepositInfo] = useState<DepositInfo | null>(null);

  useEffect(() => {
    listProviders()
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  const copyToClipboard = (value: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    notify('Copied to clipboard', { type: 'info' });
  };

  const openDeposit = (record: any) => {
    setDepositInfo({
      motherAddress: record.address,
      tokenName: record.symbol,
      tokenAddress: record.tokenAddress,
      isNative: !!record.isNative,
      chainName: record.chainName,
    });
  };

  const HotWalletDatagrid = () => {
    const { data = [] } = useListContext();

    const datagridKey = useMemo(
      () =>
        data
          .map((record: any) =>
            [
              record.provider,
              record.chainId,
              record.address,
              record.tokenAddress || record.symbol,
              record.balance,
              record.alertThreshold,
              record.alertEnabled,
              record.rebalanceCeiling,
              record.rebalanceTarget,
              record.rebalanceEnabled,
            ].join(':')
          )
          .sort()
          .join('|'),
      [data]
    );

    return (
      <Datagrid
        rowClick={false}
        bulkActionButtons={false}
        empty={<EmptyContent />}
        sx={{
          '& .RaDatagrid-headerCell': { fontWeight: 600 },
          '& .RaDatagrid-rowCell': { py: 1.25 },
        }}
        key={datagridKey}
      >
        <FunctionField
          label="module.xbitRelayHotWallets.chain"
          sortable={false}
          render={(record: any) => (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip
                label={record.chainId}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Typography variant="body2">{record.chainName}</Typography>
            </Stack>
          )}
        />
        <FunctionField
          label="module.xbitRelayHotWallets.address"
          sortable={false}
          render={(record: any) => (
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' },
              }}
              title={record.address}
              onClick={() => copyToClipboard(record.address)}
            >
              {truncateAddress(record.address)}
            </Typography>
          )}
        />
        <FunctionField
          label="module.xbitRelayHotWallets.token"
          sortable={false}
          render={(record: any) => (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {record.symbol || '-'}
              </Typography>
              {record.isNative && (
                <Chip
                  label={t('module.xbitRelayHotWallets.native')}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
              )}
            </Stack>
          )}
        />
        <FunctionField
          label="module.xbitRelayHotWallets.balance"
          sortable={false}
          render={(record: any) => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
              {record.balance ?? '-'}
            </Typography>
          )}
        />
        <FunctionField
          label={
            <ColumnHeader
              label="module.xbitRelayHotWallets.alertThreshold"
              hint="module.xbitRelayHotWallets.alertThresholdHint"
            />
          }
          sortable={false}
          render={(record: any) => <InlineThresholdEdit key={record.id} record={record} />}
        />
        <FunctionField
          label={
            <ColumnHeader
              label="module.xbitRelayHotWallets.alertEnabled"
              hint="module.xbitRelayHotWallets.alertEnabledHint"
            />
          }
          sortable={false}
          render={(record: any) => <InlineEnabledToggle key={record.id} record={record} />}
        />
        <FunctionField
          label={
            <ColumnHeader
              label="module.xbitRelayHotWallets.rebalanceCeiling"
              hint="module.xbitRelayHotWallets.rebalanceCeilingHint"
            />
          }
          sortable={false}
          render={(record: any) => <InlineCeilingEdit key={record.id} record={record} />}
        />
        <FunctionField
          label={
            <ColumnHeader
              label="module.xbitRelayHotWallets.rebalanceTarget"
              hint="module.xbitRelayHotWallets.rebalanceTargetHint"
            />
          }
          sortable={false}
          render={(record: any) => <InlineTargetEdit key={record.id} record={record} />}
        />
        <FunctionField
          label={
            <ColumnHeader
              label="module.xbitRelayHotWallets.rebalanceEnabled"
              hint="module.xbitRelayHotWallets.rebalanceEnabledHint"
            />
          }
          sortable={false}
          render={(record: any) => <InlineRebalanceToggle key={record.id} record={record} />}
        />
        <FunctionField
          label=""
          sortable={false}
          render={(record: any) => (
            <Button size="small" variant="outlined" onClick={() => openDeposit(record)}>
              {t('module.xbitRelayHotWallets.deposit')}
            </Button>
          )}
        />
      </Datagrid>
    );
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_HOT_WALLETS_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={t('module.xbitRelayHotWallets.title')}
          links={[
            { name: t('resource.xbitRelay'), href: '#' },
            { name: t('module.xbitRelayHotWallets.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            perPage={25}
            exporter={false}
            filters={<ListFilter providers={providers} />}
            filterDefaultValues={{ provider: DEFAULT_PROVIDER }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <HotWalletDatagrid />
            </Box>
          </List>
        </Card>

        <Dialog open={!!depositInfo} onClose={() => setDepositInfo(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('module.xbitRelayHotWallets.depositInfo')}</DialogTitle>
          <DialogContent dividers>
            {depositInfo && (
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1 }}>
                    <QRCodeCanvas value={depositInfo.motherAddress || ''} size={180} level="M" />
                  </Box>
                </Box>
                <Field
                  label={t('module.xbitRelayHotWallets.chain')}
                  value={depositInfo.chainName || '-'}
                />
                <Field
                  label={t('module.xbitRelayHotWallets.motherAddress')}
                  value={depositInfo.motherAddress}
                  highlightEnds
                  onCopy={() => copyToClipboard(depositInfo.motherAddress)}
                />
                <Field
                  label={t('module.xbitRelayHotWallets.tokenName')}
                  value={depositInfo.tokenName || '-'}
                />
                <Field
                  label={t('module.xbitRelayHotWallets.tokenContractAddress')}
                  value={
                    depositInfo.isNative
                      ? t('module.xbitRelayHotWallets.nativeToken')
                      : depositInfo.tokenAddress
                  }
                  highlightEnds={!depositInfo.isNative}
                  onCopy={
                    depositInfo.isNative
                      ? undefined
                      : () => copyToClipboard(depositInfo.tokenAddress)
                  }
                />
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

type FieldProps = {
  label: string;
  value: string;
  highlightEnds?: boolean;
  onCopy?: () => void;
};

const renderHighlighted = (value: string) => {
  if (!value || value.length <= 12) return value;
  const head = value.slice(0, 6);
  const tail = value.slice(-6);
  const middle = value.slice(6, -6);
  return (
    <>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {head}
      </Box>
      {middle}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {tail}
      </Box>
    </>
  );
};

const Field = ({ label, value, highlightEnds, onCopy }: FieldProps) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
        {highlightEnds ? renderHighlighted(value) : value}
      </Typography>
      {onCopy && (
        <IconButton size="small" onClick={onCopy}>
          <MdContentCopy size={16} />
        </IconButton>
      )}
    </Stack>
  </Box>
);

export default XbitRelayHotWalletsList;
